import { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { testimonialAPI, BASE_URL, API_URL } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' },
  searchInput: { flex: 1, maxWidth: '300px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  filterSelect: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', color: '#475569' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b' },
  thumbnail: { width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', backgroundColor: '#f1f5f9' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  publishedStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  draftStatus: { backgroundColor: '#fef3c7', color: '#92400e' },
  actionButtons: { display: 'flex', gap: '8px' },
  iconButton: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  button: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primaryButton: { backgroundColor: '#4f46e5', color: '#fff' },
  secondaryButton: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonActive: { backgroundColor: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' }
};

const defaultFormData = { name: '', image: '', rating: 5, text: '', date: '', isPublished: false, sortOrder: 0 };

const StarDisplay = ({ rating }) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= rating ? '#facc15' : '#e5e7eb', fontSize: '16px' }}>&#9733;</span>
    ))}
  </div>
);

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const response = await testimonialAPI.getAll(params);
      if (response.data.data) {
        setItems(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({ ...defaultFormData });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = async (item) => {
    try {
      const response = await testimonialAPI.getById(item.id);
      const data = response.data;
      setEditing(data);
      setFormData({
        name: data.name || '',
        image: data.image || '',
        rating: data.rating || 5,
        text: data.text || '',
        date: data.date || '',
        isPublished: data.isPublished || false,
        sortOrder: data.sortOrder || 0
      });
      setFormError('');
      setShowModal(true);
    } catch (err) {
      setError('Failed to load testimonial details');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.text) {
      setFormError('Name and review text are required');
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      if (editing) {
        await testimonialAPI.update(editing.id, formData);
      } else {
        await testimonialAPI.create(formData);
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await testimonialAPI.delete(id);
      setDeleteConfirm(null);
      fetchItems();
    } catch (err) {
      setError('Failed to delete testimonial');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await testimonialAPI.togglePublish(id);
      fetchItems();
    } catch (err) {
      setError('Failed to toggle publish status');
    }
  };

  if (loading && items.length === 0) {
    return <div style={styles.container}><p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Loading...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Testimonials</h1>
          <p style={styles.subtitle}>Manage user reviews and testimonials</p>
        </div>
        <button style={styles.addButton} onClick={openCreateModal}>+ Add Testimonial</button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            style={styles.searchInput}
            placeholder="Search testimonials..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <select
            style={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        {items.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#11088;</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No testimonials found</div>
            <p>Click "+ Add Testimonial" to create your first review.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Photo</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Rating</th>
                    <th style={styles.th}>Review</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Order</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.id}</td>
                      <td style={styles.td}>
                        {item.image ? (
                          <img src={item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`} alt="" style={styles.thumbnail} />
                        ) : (
                          <div style={{ ...styles.thumbnail, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4f46e5', color: '#fff', fontWeight: '600', fontSize: '18px' }}>
                            {item.name?.charAt(0)}
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.td, fontWeight: '500' }}>{item.name}</td>
                      <td style={styles.td}><StarDisplay rating={item.rating} /></td>
                      <td style={{ ...styles.td, maxWidth: '250px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</div>
                      </td>
                      <td style={styles.td}>{item.date || '-'}</td>
                      <td style={styles.td}>
                        <span
                          style={{ ...styles.statusBadge, ...(item.isPublished ? styles.publishedStatus : styles.draftStatus) }}
                          onClick={() => handleTogglePublish(item.id)}
                        >
                          {item.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td style={styles.td}>{item.sortOrder}</td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button style={styles.iconButton} onClick={() => openEditModal(item)} title="Edit">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button style={{ ...styles.iconButton, color: '#ef4444' }} onClick={() => setDeleteConfirm(item)} title="Delete">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <span style={styles.pageInfo}>Showing {items.length} of {total}</span>
                <div style={styles.pageButtons}>
                  <button style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
                  <span style={{ ...styles.pageButton, ...styles.pageButtonActive }}>{page}</span>
                  <button style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>{editing ? 'Edit Testimonial' : 'Add Testimonial'}</span>
              <button style={{ ...styles.iconButton, fontSize: '20px' }} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input style={styles.input} placeholder="User name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Review Text *</label>
                <textarea style={{ ...styles.textarea, minHeight: '100px' }} placeholder="Write the testimonial..." value={formData.text} onChange={(e) => setFormData({ ...formData, text: e.target.value })} rows={4} />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Rating</label>
                  <select style={styles.input} value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}>
                    {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Date</label>
                  <input style={styles.input} placeholder="e.g. 21 Jun 2025" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/upload/blog-image`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: fd
                      });
                      const data = await res.json();
                      if (data.success) setFormData(prev => ({ ...prev, image: `/uploads/blogs/${data.filename}` }));
                    } catch (err) {
                      console.error('Upload failed:', err);
                    }
                  }}
                  style={styles.input}
                />
                <input style={{ ...styles.input, marginTop: '6px' }} placeholder="Or paste image URL" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} />
                {formData.image && (
                  <img src={formData.image.startsWith('http') ? formData.image : `${BASE_URL}${formData.image}`} alt="Preview" style={{ marginTop: '8px', width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }} />
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Sort Order</label>
                  <input style={styles.input} type="number" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} />
                </div>
                <div style={{ ...styles.formGroup, flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.isPublished} onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })} />
                    <span style={{ fontSize: '14px', color: '#475569' }}>Published</span>
                  </label>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: saving ? 0.7 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={styles.modal} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>Delete Testimonial</span>
              <button style={{ ...styles.iconButton, fontSize: '20px' }} onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                Are you sure you want to delete the testimonial from <strong>"{deleteConfirm.name}"</strong>?
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...styles.button, backgroundColor: '#ef4444', color: '#fff' }} onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;
